import { useMemo } from "react";

import { useGetCDM } from "features/cdm/cdm/hooks/useGetCDM";
import {
  isCdm,
  isDeregistration,
  isOcf,
  isPp,
  REVIEW_TYPES,
} from "features/cdm/constants/reviewTypes";
import { useCdmReviewDiseaseIds } from "features/cdm/hooks/useCdmReviewDiseaseIds";
import { useGetOCF } from "features/cdm/ocf/hooks/useGetOCF";
import { useGetPP } from "features/cdm/pp/hooks/useGetPP";
import { isCdmReviewEligible } from "features/cdm/utils/cdmEligibility";
import { getLatestReviewId } from "features/cdm/utils/getLatestReviewId";
import { getMostRecentReviewIdByProgramme } from "features/cdm/utils/reviewProgramme";

// Locally-added (not-yet-submitted) reviews merged ahead of the BE history.
const mergeTemporaryWithHistory = (temporary, reviewHistory, belongsTo) => {
  if (!reviewHistory || reviewHistory.length <= 0) return temporary;

  return [
    ...temporary,
    ...reviewHistory.filter((item) => belongsTo(item.reviewType)),
  ];
};

const sortByReviewDateDesc = (a, b) =>
  new Date(b.reviewDate ?? b.date ?? 0) - new Date(a.reviewDate ?? a.date ?? 0);

// All CDM review data derived from the BE status + the patient's locally-added
// (temporary) reviews, which are owned by the per-programme add-review hooks and
// passed in: the per-programme lists (temporary + submitted), the Private-patient
// visibility filter, the latest/most-recent ids, and the registration prefill
// codes carried over from the most recent OCF/PP reviews (KIN-1162).
export const useCdmReviews = ({
  cdmStatus,
  financialClass,
  activeReviewId,
  temporaryOcf = [],
  temporaryPp = [],
  temporaryCdm = [],
}) => {
  const reviewHistory = cdmStatus?.reviewHistory;

  const allCDM = useMemo(
    () => mergeTemporaryWithHistory(temporaryCdm, reviewHistory, isCdm),
    [temporaryCdm, reviewHistory]
  );
  const allOCF = useMemo(
    () => mergeTemporaryWithHistory(temporaryOcf, reviewHistory, isOcf),
    [temporaryOcf, reviewHistory]
  );
  const allPP = useMemo(
    () => mergeTemporaryWithHistory(temporaryPp, reviewHistory, isPp),
    [temporaryPp, reviewHistory]
  );
  const allDeregistrations = useMemo(
    () =>
      (reviewHistory ?? []).filter((item) => isDeregistration(item.reviewType)),
    [reviewHistory]
  );

  // The review currently open in a preview/form, across all programmes.
  const activeItem = useMemo(
    () =>
      allCDM.find((item) => item.id === activeReviewId) ||
      allOCF.find((item) => item.id === activeReviewId) ||
      allPP.find((item) => item.id === activeReviewId) ||
      allDeregistrations.find((item) => item.id === activeReviewId),
    [allCDM, allOCF, allPP, allDeregistrations, activeReviewId]
  );

  // A Private patient may only be on the Diabetes CDM programme — hide any
  // existing non-Diabetes CDM reviews. Temporary reviews carry their diagnoses
  // in registrationReasons; submitted reviews need a per-review code lookup.
  const cdmDiseaseIdsByReview = useCdmReviewDiseaseIds(reviewHistory);
  const visibleCDM = useMemo(
    () =>
      allCDM.filter((item) =>
        isCdmReviewEligible(
          item.registrationReasons ?? cdmDiseaseIdsByReview[item.id],
          financialClass
        )
      ),
    [allCDM, cdmDiseaseIdsByReview, financialClass]
  );

  const latestReviewId = useMemo(
    () => getLatestReviewId(reviewHistory),
    [reviewHistory]
  );
  const mostRecentReviewIdByProgramme = useMemo(
    () => getMostRecentReviewIdByProgramme(reviewHistory ?? []),
    [reviewHistory]
  );

  const { latestOcfId, latestPpId, latestPpRegistrationId, latestCdmId } =
    useMemo(() => {
      const history = reviewHistory ?? [];
      const latestOcf = [...history]
        .filter((review) => isOcf(review.reviewType))
        .sort(sortByReviewDateDesc)[0];
      const latestPp = [...history]
        .filter((review) => isPp(review.reviewType))
        .sort(sortByReviewDateDesc)[0];
      const latestPpRegistration = [...history]
        .filter((review) => review.reviewType === REVIEW_TYPES.PP_REGISTRATION)
        .sort(sortByReviewDateDesc)[0];
      const latestCdm = [...history]
        .filter((review) => isCdm(review.reviewType))
        .sort(sortByReviewDateDesc)[0];

      return {
        latestOcfId: latestOcf?.id ?? null,
        latestPpId: latestPp?.id ?? null,
        latestPpRegistrationId: latestPpRegistration?.id ?? null,
        latestCdmId: latestCdm?.id ?? null,
      };
    }, [reviewHistory]);

  const { data: latestOcf } = useGetOCF(latestOcfId);
  const { data: latestPp } = useGetPP(latestPpId);
  const { data: latestPpRegistration } = useGetPP(latestPpRegistrationId);
  const { data: latestCdm } = useGetCDM(latestCdmId);

  // CDM registration diagnoses prefill: the most recent OCF/PP review whose
  // outcome was "diagnosed with chronic disease". The transferred per-disease
  // year map travels alongside the codes (empty until BE ships it — KIN-1406).
  const { cdmPrefillCodes, cdmPrefillYears } = useMemo(() => {
    const candidates = [];

    if (latestOcf?.outcome === "DIAGNOSED_WITH_CHRONIC_DISEASE") {
      candidates.push({
        date: latestOcf.reviewDate,
        codes: latestOcf.cdmDiagnosisCodes ?? [],
        years: latestOcf.cdmDiagnosisYears ?? {},
      });
    }

    if (latestPp?.diagnosedWithChronicDisease === "YES") {
      candidates.push({
        date: latestPp.reviewDate,
        codes: latestPp.cdmDiagnosisCodes ?? [],
        years: latestPp.cdmDiagnosisYears ?? {},
      });
    }

    if (!candidates.length) return { cdmPrefillCodes: [], cdmPrefillYears: {} };

    candidates.sort((a, b) => new Date(b.date ?? 0) - new Date(a.date ?? 0));

    return {
      cdmPrefillCodes: candidates[0].codes,
      cdmPrefillYears: candidates[0].years,
    };
  }, [latestOcf, latestPp]);

  // CDM interim/annual diagnoses prefill: the conditions carried on the
  // patient's most recent CDM review. A subsequent interim/annual review covers
  // the already-registered diseases (CDM spec, "Adding a new diagnosis after
  // first registration"), so they are pre-checked; the dialog intersects these
  // with the currently-eligible conditions before ticking them.
  const cdmInterimAnnualPrefillCodes = useMemo(
    () =>
      (latestCdm?.registrationReasons ?? [])
        .map((diagnosis) => diagnosis?.disease)
        .filter(Boolean),
    [latestCdm]
  );

  // The prior CDM review already stores a per-disease year map — carry it into
  // the interim/annual prefill so the year travels with the code (not dropped).
  const cdmInterimAnnualPrefillYears = latestCdm?.yearOfDiagnosis ?? {};

  // PP registration reasons prefill: the most recent OCF whose outcome was
  // "register on prevention programme". The transferred per-code year map
  // travels alongside (empty until BE ships it — KIN-1406).
  const isOcfPpTransfer =
    latestOcf?.outcome === "REGISTER_ON_PREVENTION_PROGRAMME";
  const ppPrefillCodes = isOcfPpTransfer
    ? latestOcf.preventionReasonCodes ?? []
    : [];
  const ppPrefillYears = isOcfPpTransfer
    ? latestOcf.preventionReasonYears ?? {}
    : {};

  // PP annual-review reasons carry over from the most recent PP registration,
  // together with its single shared year of registration.
  const ppAnnualPrefillReasonCodes =
    latestPpRegistration?.registrationReasons ?? [];
  const ppAnnualPrefillYearOfRegistration =
    latestPpRegistration?.yearOfRegistration ?? null;

  // PP additional risk factors (code + per-factor year) carry over from the
  // most recent PP review of any type, so factors added at a prior annual are
  // retained rather than silently dropped.
  const ppAnnualPrefillAdditionalRiskFactors =
    latestPp?.additionalRiskFactors ?? [];
  const ppAnnualPrefillRiskFactorYears =
    latestPp?.yearOfAdditionalRiskFactor ?? {};

  return {
    allCDM,
    visibleCDM,
    allOCF,
    allPP,
    allDeregistrations,
    activeItem,
    latestReviewId,
    mostRecentReviewIdByProgramme,
    cdmPrefillCodes,
    cdmPrefillYears,
    cdmInterimAnnualPrefillCodes,
    cdmInterimAnnualPrefillYears,
    ppPrefillCodes,
    ppPrefillYears,
    ppAnnualPrefillReasonCodes,
    ppAnnualPrefillYearOfRegistration,
    ppAnnualPrefillAdditionalRiskFactors,
    ppAnnualPrefillRiskFactorYears,
  };
};
